import Routes from '@internal/config/routes';
import {
  ApplicationState,
  Project,
  ProjectDeclineStage,
  ProjectOfferStatus,
  ProjectPositionDecision,
} from '@internal/types/projects';

import {
  isInternalPath,
  isProjectClosed,
  PROJECT_FROM_QUERY_KEY,
} from './projects.helpers';

const APPLIED_STATES: ReadonlySet<ApplicationState> = new Set([
  ApplicationState.InReview,
  ApplicationState.WithMatching,
  ApplicationState.UnderClientReview,
  ApplicationState.Accepted,
  ApplicationState.NotSelected,
  ApplicationState.Withdrawn,
  ApplicationState.ProjectClosed,
  ApplicationState.OfferCancelled,
]);

const ACTIVE_STATES: ReadonlySet<ApplicationState> = new Set([
  ApplicationState.InReview,
  ApplicationState.WithMatching,
  ApplicationState.UnderClientReview,
]);

const TERMINAL_STATES: ReadonlySet<ApplicationState> = new Set([
  ApplicationState.Accepted,
  ApplicationState.NotSelected,
  ApplicationState.Withdrawn,
  ApplicationState.ProjectClosed,
  ApplicationState.OfferCancelled,
]);

const CLOSURE_IMMUNE_STATES: ReadonlySet<ApplicationState> = new Set([
  ApplicationState.Accepted,
  ApplicationState.NotSelected,
  ApplicationState.Withdrawn,
  ApplicationState.OfferDeclined,
]);

const stateFromWire = (project: Project): ApplicationState | null => {
  const applied = Boolean(project.hasCandidateConfirmedApplication);
  const status = project.offerStatus;

  if (!status && !applied) return null;

  switch (status) {
    case ProjectOfferStatus.OfferAccepted:
      return ApplicationState.Accepted;
    case ProjectOfferStatus.UnderReview:
      return ApplicationState.UnderClientReview;
    case ProjectOfferStatus.OfferDeclinedByDev:
      return applied
        ? ApplicationState.Withdrawn
        : ApplicationState.OfferDeclined;
    case ProjectOfferStatus.OfferDeclined:
    case ProjectOfferStatus.OfferDeclinedOnClientSuccessStage:
      return ApplicationState.NotSelected;
    default:
      if (!applied) return ApplicationState.Offered;
      return project.positionDecision === ProjectPositionDecision.Pass
        ? ApplicationState.WithMatching
        : ApplicationState.InReview;
  }
};

const isClosureDecline = (project: Project): boolean =>
  project.offerStatus === ProjectOfferStatus.OfferDeclined &&
  project.declineStage === ProjectDeclineStage.Recruiting &&
  project.positionDecision !== ProjectPositionDecision.Reject &&
  !project.declineFeedback?.trim();

export const getApplicationState = (
  project: Project
): ApplicationState | null => {
  const state = stateFromWire(project);
  if (!state) return null;
  if (!isProjectClosed(project)) return state;
  if (state === ApplicationState.Offered)
    return ApplicationState.OfferCancelled;
  if (state === ApplicationState.NotSelected && isClosureDecline(project)) {
    return ApplicationState.ProjectClosed;
  }
  return CLOSURE_IMMUNE_STATES.has(state)
    ? state
    : ApplicationState.ProjectClosed;
};

export const isClosedProject = (project: Project): boolean => {
  const state = getApplicationState(project);
  return (
    state === ApplicationState.ProjectClosed ||
    state === ApplicationState.OfferCancelled ||
    (!state && isProjectClosed(project))
  );
};

export const isAppliedState = (state: ApplicationState | null): boolean =>
  Boolean(state && APPLIED_STATES.has(state));

export const hasSubmittedApplication = (project: Project): boolean =>
  Boolean(project.hasCandidateConfirmedApplication);

export const isClosedUnsubmittedOffer = (project: Project): boolean => {
  if (!isProjectClosed(project) || hasSubmittedApplication(project)) {
    return false;
  }
  const state = getApplicationState(project);
  return state === null || state === ApplicationState.OfferCancelled;
};

export const isActiveApplication = (state: ApplicationState | null): boolean =>
  Boolean(state && ACTIVE_STATES.has(state));

/** Terminal without a win: closed, declined, withdrawn or cancelled. */
export const isEndedApplication = (state: ApplicationState): boolean =>
  isTerminalApplication(state) && state !== ApplicationState.Accepted;

/** The CRM closed a running subscription rather than declining the application. */
export const isSubscriptionClosed = (
  state: ApplicationState,
  project: Project
): boolean =>
  state === ApplicationState.NotSelected &&
  project.offerStatus === ProjectOfferStatus.OfferDeclinedOnClientSuccessStage;

export const isTerminalApplication = (
  state: ApplicationState | null
): boolean => Boolean(state && TERMINAL_STATES.has(state));

export const APPLICATION_STATE_LABELS: Record<ApplicationState, string> = {
  [ApplicationState.Offered]: 'New offer',
  [ApplicationState.OfferDeclined]: 'Offer declined',
  [ApplicationState.OfferCancelled]: 'Cancelled offer',
  [ApplicationState.InReview]: 'In review',
  [ApplicationState.WithMatching]: 'Client review preparation',
  [ApplicationState.UnderClientReview]: 'Client review in progress',
  [ApplicationState.Accepted]: 'Selected by client',
  [ApplicationState.NotSelected]: 'Not selected',
  [ApplicationState.Withdrawn]: 'Withdrawn',
  [ApplicationState.ProjectClosed]: 'Project closed',
};

type FromQuery = string | string[] | undefined;

const isMyApplicationsList = (value: string): boolean =>
  value === Routes.MyApplications ||
  value.startsWith(`${Routes.MyApplications}?`);

const readInternalPath = (from: FromQuery): string | null => {
  const value = Array.isArray(from) ? from[0] : from;
  return value && isInternalPath(value) ? value : null;
};

const getApplicationsOrigin = (from: FromQuery): string | null => {
  const value = readInternalPath(from);
  if (!value) return null;
  if (isMyApplicationsList(value)) return value;
  if (value.startsWith(`${Routes.MyApplications}/`)) {
    const nested = new URLSearchParams(value.split('?')[1] ?? '').get(
      PROJECT_FROM_QUERY_KEY
    );
    return nested && isMyApplicationsList(nested) ? nested : null;
  }
  return null;
};

export const getApplicationDealId = (project: Project): string =>
  project.applicationProjectDealId || project.dealId;

export const getApplicationHref = (
  project: Project,
  from?: FromQuery
): string => {
  const origin = getApplicationsOrigin(from);
  const query = origin
    ? `?${PROJECT_FROM_QUERY_KEY}=${encodeURIComponent(origin)}`
    : '';
  return `${Routes.MyApplications}/${getApplicationDealId(project)}${query}`;
};

export const getApplicationBackHref = (from: FromQuery): string =>
  getApplicationsOrigin(from) ?? Routes.MyApplications;

export const getApplicationReturnHref = (from: FromQuery): string | null => {
  const value = readInternalPath(from);
  return value?.startsWith(`${Routes.MyApplications}/`) ? value : null;
};
